/** One canvas drawn into whatever page mounts it, from state that page holds. */

import type { CueFrame } from "@nebulize/sdk";
import { Application } from "pixi.js";
import { useEffect, useRef } from "react";

import type { PickTarget } from "../pixiScene";
import { createPixiSceneBackend } from "../pixiScene";
import type { SceneState } from "../scene/attachScene";
import { pushScene } from "../scene/attachScene";
import type { PushedScene } from "../scene/attachScene";
import { sceneReport } from "../scene/report";
import type { Box, RenderNode } from "../scene/sceneTree";
import { browserSurfacePorts } from "../surface/browserPorts";
import type { SurfaceCatalogue } from "../surface/browserPorts";
import { cloudSurfaceCatalogue } from "../surface/surfaceCatalogue";
import { SurfaceLayer } from "../surface/surfaceLayer";
import { createCueMemory } from "../transport/cueMemory";

/** What a click on a canvas reports. The host names the activity; this names what was hit. */
export interface PickedNode {
  /** The activity whose document drew this. */
  readonly activityId: string;
  /** The canvas kind of the document that drew it. */
  readonly canvasKind: string;
  /** The authored id of the node hit, or undefined for the document's own empty space. */
  readonly nodeId: string | undefined;
  /** The drawn instance hit, unique across repeats. Undefined for the document itself. */
  readonly nodeKey: string | undefined;
  /** The node kind drawn, or undefined for the document itself. */
  readonly nodeKind: string | undefined;
  /** The box hit, in canvas fractions, as the scene placed it. */
  readonly box: Box;
  /** What this node sends when pressed, or undefined for one nothing presses. */
  readonly action: { readonly submission: string; readonly value: string } | undefined;
}

export interface CanvasViewProps {
  /** Which canvas kind to draw. A document for another kind is not drawn here. */
  readonly canvasKind: string;
  readonly state: SceneState;
  /** Fills the element it is given. The host decides how big that is. */
  readonly className?: string;
  /** Report what a click reached. Absent leaves the canvas inert, as a broadcast wants. */
  readonly onPick?: (picked: PickedNode) => void;
  /** The last cue the host's transport handed it. Played once per `envelope_id`, then never. */
  readonly cue?: CueFrame;
  /**
   * Where OVL-016 surface modules are fetched from. Defaults to this page's own origin, which is
   * right wherever the canvas router is served or proxied there. Read once, at mount.
   */
  readonly surfaces?: SurfaceCatalogue;
}

/** One pick, in the shape a host reports on: the document always, the node when there was one. */
export function pickedFrom(node: RenderNode, target: PickTarget): PickedNode {
  const document = {
    activityId: node.activityId,
    canvasKind: node.canvasKind,
    box: node.box,
  };

  if (target === "document") {
    return { ...document, nodeId: undefined, nodeKey: undefined, nodeKind: undefined, action: undefined };
  }
  return { ...document, nodeId: node.id, nodeKey: node.key, nodeKind: node.kind, action: node.action };
}

/** Whether this browser can hand a canvas to a worker at all. */
export function canDrawSurfaces(): boolean {
  if (typeof Worker === "undefined" || typeof HTMLCanvasElement === "undefined") return false;
  return "transferControlToOffscreen" in HTMLCanvasElement.prototype;
}

/** A surface host for this element, or undefined on a browser that cannot run one. */
function surfaceLayerFor(
  parent: HTMLElement,
  catalogue: SurfaceCatalogue | undefined,
): SurfaceLayer | undefined {
  if (!canDrawSurfaces()) return undefined;
  return new SurfaceLayer(
    browserSurfacePorts(parent, catalogue ?? cloudSurfaceCatalogue(globalThis.location.origin)),
  );
}

export function CanvasView({ canvasKind, state, className, onPick, cue, surfaces }: CanvasViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<PushedScene | null>(null);
  const playedRef = useRef(createCueMemory());
  const heldRef = useRef<SceneState>(state);
  heldRef.current = state;
  const pickRef = useRef<((picked: PickedNode) => void) | undefined>(onPick);
  pickRef.current = onPick;
  const surfacesRef = useRef<SurfaceCatalogue | undefined>(surfaces);
  surfacesRef.current = surfaces;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let disposed = false;
    let ready = false;
    let layer: SurfaceLayer | undefined;
    const app = new Application();

    void app
      .init({ backgroundAlpha: 0, antialias: true, resizeTo: container, preference: "webgl" })
      .then(() => {
        if (disposed) {
          app.destroy(true);
          return;
        }
        ready = true;
        container.appendChild(app.canvas);
        layer = surfaceLayerFor(container, surfacesRef.current);
        const held = layer;
        const scene = pushScene(
          {
            onResize: (listener) => {
              app.renderer.on("resize", listener);
              return () => {
                app.renderer.off("resize", listener);
              };
            },
            onEntities: (entities, stale) => {
              held?.push(entities, stale);
            },
          },
          createPixiSceneBackend(
            app.stage,
            () => ({ width: app.renderer.screen.width, height: app.renderer.screen.height }),
            {
              ...(held === undefined ? {} : { surfaces: held }),
              ...(pickRef.current === undefined
                ? {}
                : {
                    onPick: (node: RenderNode, target: PickTarget) => {
                      pickRef.current?.(pickedFrom(node, target));
                    },
                  }),
            },
          ),
          canvasKind,
        );
        sceneRef.current = scene;
        scene.apply(heldRef.current);
        app.renderer.on("resize", () => {
          scene.redraw();
        });
      })
      .catch(() => {
        // No renderer here. A host with no canvas draws nothing and says so through its own
        // empty state; an unhandled rejection would take the page down instead.
        disposed = true;
      });

    return () => {
      disposed = true;
      sceneRef.current?.clear();
      sceneRef.current = null;
      layer?.closeAll();
      layer = undefined;
      // Only a started application can be destroyed: unmounting before `init` settles leaves it
      // half built, and the `disposed` branch above tears that one down instead.
      if (ready) app.destroy(true, { children: true, texture: true });
    };
  }, [canvasKind]);

  useEffect(() => {
    sceneRef.current?.apply(state);
  }, [state]);

  useEffect(() => {
    if (cue === undefined) return;
    if (playedRef.current.saw(cue.envelope_id)) return;
    // Remembered before it is played, so the drop below is permanent rather than deferred.
    playedRef.current.remember(cue.envelope_id);

    const scene = sceneRef.current;
    if (scene === null) {
      sceneReport.once("cue:no-canvas", `cue "${cue.cue.tag}" arrived before this canvas drew`);
      return;
    }
    scene.present(cue);
  }, [cue]);

  return <div ref={containerRef} className={className} style={{ position: "absolute", inset: 0 }} />;
}
